import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, getRouteApi, useNavigate } from '@tanstack/react-router';
import {
    Alert,
    Anchor,
    Button,
    Group,
    Loader,
    Modal,
    ScrollArea,
    Skeleton,
    Stack,
    Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconChevronLeft, IconPencil, IconTrash } from '@tabler/icons-react';
import { listCpeMappings } from '../api/cpe-mappings';
import { listVendorAliases } from '../api/vendor-aliases';
import { bulkDeleteCompanies, getVendorByCandidateId, updateVendor } from '../api/vendors';
import { CpeMappingsCard } from '../components/CpeMappingsCard';
import { CveMonitoringCard } from '../components/CveMonitoringCard';
import { OsintReportCard } from '../components/OsintReportCard';
import { VendorAliasesCard } from '../components/VendorAliasesCard';
import { VendorFormModal, type VendorFormValues } from '../components/VendorFormModal';
import { VendorInfoCard } from '../components/VendorInfoCard';
import { useIsAdmin } from '../hooks/useIsAdmin';

/** Trim an optional text field; empty → undefined so we omit it from the payload. */
const optional = (value: string): string | undefined => {
    const trimmed = value.trim();
    return trimmed === '' ? undefined : trimmed;
};

// Route-bound param access. The `$candidateId` segment is parsed to a number at
// the route (see router.tsx), so `candidateId` arrives here already typed as the
// numeric candidate id — the canonical key (F3) for the detail fetch (VI-472)
// and all three sibling CPE/alias/CVE cards (VI-473). Unlike the compound id
// (which degrades to `::<slug>` and collides on `::default` across synthesized
// rows), it uniquely identifies the vendor.
const route = getRouteApi('/catalog/$candidateId');

/**
 * Vendor detail view: vendor info card, OSINT report, CPE mappings, vendor
 * aliases, CVE monitoring. Fetches in parallel; loading and error states surface via Mantine.
 * Rendered at `/catalog/$candidateId`; the breadcrumb navigates back to the
 * `/catalog` list.
 */
export const VendorDetailView = (): React.JSX.Element => {
    const { candidateId } = route.useParams();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const { data: adminStatus } = useIsAdmin();
    const isAdmin = adminStatus?.is_admin ?? false;
    const [editOpen, setEditOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);

    const {
        data: vendor,
        isLoading: isVendorLoading,
        error: vendorError,
    } = useQuery({
        queryKey: ['vendor', candidateId],
        queryFn: () => getVendorByCandidateId(candidateId),
    });

    const updateMutation = useMutation({
        mutationFn: (values: VendorFormValues) =>
            updateVendor(candidateId, {
                // Required fields — always present (validated non-empty in the form).
                name: values.name.trim(),
                provider_abbreviation: values.provider_abbreviation.trim(),
                organization_id: values.organization_id.trim(),
                slug: values.slug.trim(),
                is_default: values.is_default,
                // Clearable fields — send the trimmed value INCLUDING '' so the
                // server clears them (→ null); the form always submits the full
                // record, so empty here means "remove it".
                domain: values.domain.trim(),
                website_url: values.website_url.trim(),
                open_trust_url: values.open_trust_url.trim(),
                trust_center_url: values.trust_center_url.trim(),
                logo: values.logo.trim(),
                favicon: values.favicon.trim(),
                metadata: values.metadata.trim(),
                // Product name can't be blank server-side (min 1); empty leaves it unchanged.
                product_name: optional(values.product_name),
            }),
        onSuccess: (updated) => {
            setEditOpen(false);
            void queryClient.invalidateQueries({ queryKey: ['vendor', candidateId] });
            void queryClient.invalidateQueries({ queryKey: ['vendors'] });
            notifications.show({
                color: 'green',
                title: 'Vendor updated',
                message: `${updated.name} was updated.`,
            });
        },
        onError: (err: unknown) => {
            notifications.show({
                color: 'red',
                title: 'Could not update vendor',
                message: err instanceof Error ? err.message : 'Unknown error',
            });
        },
    });

    // Deletes the WHOLE company (its products + candidates), same as the grid's
    // bulk delete. On success we leave the (now-gone) detail page for the list.
    const deleteMutation = useMutation({
        mutationFn: () => bulkDeleteCompanies([candidateId]),
        onSuccess: (result) => {
            setDeleteOpen(false);
            // The endpoint returns 200 with per-id buckets; a rejected id lands in
            // `failed`, so guard against a false success + navigate.
            if (result.failed.length > 0) {
                notifications.show({
                    color: 'red',
                    title: 'Could not delete vendor',
                    message: result.failed[0]?.error ?? 'The vendor could not be deleted.',
                });
                return;
            }
            void queryClient.invalidateQueries({ queryKey: ['vendors'] });
            void queryClient.invalidateQueries({ queryKey: ['candidates'] });
            notifications.show({
                color: 'green',
                title: 'Vendor deleted',
                message: 'The company and its records were removed.',
            });
            void navigate({ to: '/catalog' });
        },
        onError: (err: unknown) => {
            notifications.show({
                color: 'red',
                title: 'Could not delete vendor',
                message: err instanceof Error ? err.message : 'Unknown error',
            });
        },
    });

    const { data: cpeMappingsData, isLoading: isCpeMappingsLoading } = useQuery({
        queryKey: ['cpe-mappings', candidateId],
        queryFn: () => listCpeMappings({ catalog_product_candidate_id: candidateId }),
    });

    const { data: aliasesData, isLoading: isAliasesLoading } = useQuery({
        queryKey: ['vendor-aliases', candidateId],
        queryFn: () => listVendorAliases({ catalog_product_candidate_id: candidateId }),
    });

    const cpeMappings = cpeMappingsData?.data ?? [];
    const aliases = aliasesData?.data ?? [];

    const vendorLabel = vendor
        ? `${vendor.name}${vendor.domain ? ` (${vendor.domain})` : ''}`
        : 'Loading…';

    return (
        <Stack gap="xl" flex={1} mih={0} data-testid="VendorDetailView">
            <Group gap="xs" align="center">
                <Anchor component={Link} to="/catalog" size="sm" data-testid="DetailBreadcrumbBack">
                    <Group gap={4} align="center">
                        <IconChevronLeft size={14} />
                        Company Catalog
                    </Group>
                </Anchor>
                <Text size="sm" c="dimmed">
                    /
                </Text>
                <Text size="sm" fw={500}>
                    {vendorLabel}
                </Text>
                {isAdmin && vendor ? (
                    <Group gap="xs" ml="auto">
                        <Button
                            variant="light"
                            size="xs"
                            leftSection={<IconPencil size={14} />}
                            onClick={() => {
                                setEditOpen(true);
                            }}
                            data-testid="VendorDetailEditButton"
                        >
                            Edit
                        </Button>
                        <Button
                            variant="light"
                            color="red"
                            size="xs"
                            leftSection={<IconTrash size={14} />}
                            onClick={() => {
                                setDeleteOpen(true);
                            }}
                            data-testid="VendorDetailDeleteButton"
                        >
                            Delete
                        </Button>
                    </Group>
                ) : null}
            </Group>

            {vendorError ? (
                <Alert
                    icon={<IconAlertTriangle size={16} />}
                    color="red"
                    title="Failed to load vendor details"
                    data-testid="VendorDetailError"
                >
                    {vendorError instanceof Error ? vendorError.message : 'Unknown error'}
                </Alert>
            ) : (
                <ScrollArea flex={1} mih={0}>
                    <Stack gap="xl">
                        {isVendorLoading || !vendor ? (
                            <Skeleton height={200} radius="md" />
                        ) : (
                            <VendorInfoCard vendor={vendor} />
                        )}

                        <OsintReportCard catalogProductCandidateId={candidateId} />

                        {isCpeMappingsLoading ? (
                            <Loader />
                        ) : (
                            <CpeMappingsCard
                                catalogProductCandidateId={candidateId}
                                mappings={cpeMappings}
                            />
                        )}

                        {isAliasesLoading ? (
                            <Loader />
                        ) : (
                            <VendorAliasesCard
                                catalogProductCandidateId={candidateId}
                                aliases={aliases}
                            />
                        )}

                        <CveMonitoringCard
                            catalogProductCandidateId={candidateId}
                            isMonitored={vendor?.is_monitored ?? false}
                            canMonitor={cpeMappings.length > 0}
                        />
                    </Stack>
                </ScrollArea>
            )}

            <VendorFormModal
                opened={editOpen}
                mode="edit"
                initial={
                    vendor
                        ? {
                              name: vendor.name,
                              provider_abbreviation: vendor.provider_abbreviation ?? '',
                              organization_id: vendor.organization_id ?? '',
                              domain: vendor.domain ?? '',
                              website_url: vendor.website_url ?? '',
                              open_trust_url: vendor.open_trust_url ?? '',
                              trust_center_url: vendor.trust_center_url ?? '',
                              logo: vendor.logo ?? '',
                              favicon: vendor.favicon ?? '',
                              product_name: vendor.product_name ?? '',
                              slug: vendor.slug ?? '',
                              is_default: vendor.is_default,
                              metadata: vendor.metadata ?? '',
                          }
                        : undefined
                }
                submitting={updateMutation.isPending}
                onClose={() => {
                    setEditOpen(false);
                }}
                onSubmit={(values) => {
                    updateMutation.mutate(values);
                }}
            />

            <Modal
                opened={deleteOpen}
                onClose={() => {
                    setDeleteOpen(false);
                }}
                centered
                title="Delete vendor"
                data-testid="VendorDetailDeleteModal"
            >
                <Stack gap="md">
                    <Text size="sm">
                        Delete <strong>{vendor?.name}</strong>? This removes the whole company — its
                        products and candidate records — and can’t be undone from here.
                    </Text>
                    <Group justify="flex-end" gap="sm">
                        <Button
                            variant="default"
                            onClick={() => {
                                setDeleteOpen(false);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            color="red"
                            leftSection={<IconTrash size={16} />}
                            loading={deleteMutation.isPending}
                            onClick={() => {
                                deleteMutation.mutate();
                            }}
                            data-testid="VendorDetailDeleteConfirm"
                        >
                            Delete
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </Stack>
    );
};
